import { useT } from "../../../../i18n/I18nProvider";
import type { ViewRetentionDue, ViewRetentionSettings } from "../../../../lib/salesViewRetention";
import { MAX_VIEW_RETENTION_DAYS, MIN_VIEW_RETENTION_DAYS } from "../../../../lib/salesViewRetention";
import { Card, CardHead } from "../../../ui";
import { ToggleRow } from "../panel/fields";
import { TextField } from "./fields";

/**
 * Settings -> Invoices -> visitor trail: how long a public document remembers
 * WHO opened it.
 *
 * Every opening of a public link is a row, and the row keeps the address and
 * the browser string it arrived with. The count and the timestamp are facts
 * about the document and stay forever; the trail is about a person who never
 * signed in, and this card is where an operator decides how long it is kept.
 *
 * ── THE COUNT IS FOR THE STORED POLICY, NOT THE ONE BEING TYPED ─────────────
 *
 * It comes back with the settings and is not re-fetched while the number field
 * is being edited. That is deliberate and it is the same trade the bulk export
 * makes with its per-status counts: one query per keystroke, on a page whose
 * whole job is to be read before it is saved, buys a number that is about to
 * change anyway. What the reader needs before pressing Save is the size of what
 * they are standing on, and that is what the sentence says.
 *
 * Like the three cards above it, this one reports up: the tab has one Save.
 */
export function ViewTrailCard({
  settings,
  due,
  onChange,
}: {
  settings: ViewRetentionSettings;
  due: ViewRetentionDue;
  onChange: (patch: Partial<ViewRetentionSettings>) => void;
}) {
  const t = useT();

  return (
    <Card>
      <CardHead title={t("settings.invoicing.viewTrail")} sub={t("settings.invoicing.viewTrailDesc")} />

      <div className="px-6 pb-6 pt-1">
        <ToggleRow
          label={t("settings.invoicing.viewTrailEnabled")}
          hint={t("settings.invoicing.viewTrailEnabledHint")}
          checked={settings.enabled}
          onChange={(enabled) => onChange({ enabled })}
        />

        {/* Hidden rather than greyed when the sweep is off, exactly as the VAT
            rate is above: a disabled box still asks the reader what belongs in
            it, and an age that erases nothing is not a question. */}
        {settings.enabled ? (
          <TextField
            className="mt-5 max-w-xs"
            label={t("settings.invoicing.viewTrailDays")}
            hint={t("settings.invoicing.viewTrailDaysHint")}
            type="number"
            min={String(MIN_VIEW_RETENTION_DAYS)}
            max={String(MAX_VIEW_RETENTION_DAYS)}
            inputMode="numeric"
            numeric
            value={String(settings.days)}
            onChange={(v) => onChange({ days: parseInt(v, 10) || MIN_VIEW_RETENTION_DAYS })}
          />
        ) : null}

        {/* Three sentences and not one with a number in it. A desk with no
            public links yet must not be told that "0 of 0 visits" are due -
            that reads as a broken screen rather than as an empty table. */}
        <p className="crm-fnote mt-4">
          {due.withTrail === 0
            ? t("settings.invoicing.viewTrailNone")
            : settings.enabled
              ? t("settings.invoicing.viewTrailDue", { n: due.due, total: due.withTrail })
              : t("settings.invoicing.viewTrailKept", { n: due.withTrail })}
        </p>
      </div>
    </Card>
  );
}
